/**
 * BreadcrumbCampaignSelector Component
 *
 * Replaces the last breadcrumb item with an expandable dropdown
 * that allows campaign and route selection.
 *
 * UX Design:
 * - Campaign name displays as a box/button
 * - Clicking expands like a browser tab folder
 * - Shows campaign list + active route selector
 * - Styled to indicate it's interactive
 *
 * Mobile: Double height (88px), scaled content
 * Desktop: Standard height (40px)
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Check, MapPin, Route as RouteIcon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useCampaignStore } from '@features/campaigns/store/campaignStore';
import { useRouteStore } from '@features/routes/store/routeStore';
import { Menu, MenuItem, MenuSeparator } from '@shared/components/ui';
import { RouteHistorySubmenu } from './RouteHistorySubmenu';

export interface BreadcrumbCampaignSelectorProps {
  /**
   * Whether to show route selector (default: true, available from all tabs)
   */
  showRouteSelector?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Breadcrumb Campaign Selector - Interactive dropdown in breadcrumb trail
 *
 * @example
 * ```tsx
 * // Basic campaign selector
 * <BreadcrumbCampaignSelector />
 *
 * // With route selector
 * <BreadcrumbCampaignSelector showRouteSelector={true} />
 * ```
 */
export const BreadcrumbCampaignSelector: React.FC<BreadcrumbCampaignSelectorProps> = ({
  showRouteSelector = true,
  className = '',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { campaigns, selectedCampaignId, setSelectedCampaign } = useCampaignStore();
  const {
    routeHistory,
    activeRouteId,
    activeRoutesByCampaign,
    setActiveRoute,
    fetchRouteHistory,
    loadRoute,
  } = useRouteStore();
  // Track which campaign has expanded route history submenu
  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(selectedCampaignId);
  const [isOpen, setIsOpen] = useState(false);
  const [showRoutes, setShowRoutes] = useState(false);
  const [hoveredCampaignId, setHoveredCampaignId] = useState<string | null>(null);
  const [submenuRoutes, setSubmenuRoutes] = useState<any[]>([]);
  const [drillDownCampaignId, setDrillDownCampaignId] = useState<string | null>(null);
  const [drillDownRoutes, setDrillDownRoutes] = useState<any[]>([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const bottomSheetRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  /**
   * URL Parameter Sync: Read campaign ID from URL on mount
   * Restores selected campaign from deep links (e.g., ?campaign=123)
   */
  useEffect(() => {
    const campaignParam = searchParams.get('campaign');
    if (campaignParam && campaignParam !== selectedCampaignId) {
      // Restore campaign from URL parameter
      setSelectedCampaign(campaignParam);
    }
  }, []); // Run once on mount

  /**
   * URL Parameter Sync: Update URL when campaign changes
   * Enables shareable links with campaign context
   */
  useEffect(() => {
    if (selectedCampaignId) {
      // Update URL parameter without replacing history entry
      const newParams = new URLSearchParams(searchParams);
      newParams.set('campaign', selectedCampaignId);
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedCampaignId]);

  // Get selected campaign object
  const selectedCampaign = campaigns?.find(c => c.campaign_id === selectedCampaignId);

  // Get active route - safely handle routeHistory (ensure it's an array)
  const routeHistoryArray = Array.isArray(routeHistory) ? routeHistory : [];
  const activeRoute = routeHistoryArray.find(r => r.route_id === activeRouteId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowRoutes(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Fetch route history when opening dropdown with route selector
  useEffect(() => {
    const campaignToFetch = expandedCampaignId || selectedCampaignId;
    if (isOpen && showRouteSelector && campaignToFetch) {
      fetchRouteHistory(campaignToFetch);
    }
  }, [isOpen, showRouteSelector, selectedCampaignId, expandedCampaignId, fetchRouteHistory]);

  // Auto-expand selected campaign's submenu when dropdown opens
  useEffect(() => {
    if (isOpen && selectedCampaignId) {
      setExpandedCampaignId(selectedCampaignId);
    }
  }, [isOpen, selectedCampaignId]);

  // Get status badge color (same as UnifiedAppShellHeader)
  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
      case 'planning':
        return 'bg-blue-500/20 text-blue-400 border-brand-500/50';
      case 'future':
        return 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50';
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'ongoing':
        return 'bg-teal-500/20 text-teal-400 border-teal-500/50';
      case 'in progress':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 'completed':
        return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50';
      case 'archived':
        return 'bg-gray-500/20 text-dark-muted border-gray-500/50';
      default:
        return 'bg-gray-500/20 text-dark-muted border-gray-500/50';
    }
  };

  // Sort campaigns by status priority (active first, archived last)
  const statusOrder: Record<string, number> = {
    'active': 0,
    'ongoing': 1,
    'in progress': 2,
    'future': 3,
    'planning': 4,
    'completed': 5,
    'archived': 6,
    'draft': 7
  };

  const sortedCampaigns = campaigns
    ? [...campaigns].sort((a, b) => {
        const orderA = statusOrder[a.status.toLowerCase()] ?? 99;
        const orderB = statusOrder[b.status.toLowerCase()] ?? 99;
        return orderA - orderB;
      })
    : [];

  const handleCampaignSelect = (campaignId: string) => {
    setSelectedCampaign(campaignId);
    setExpandedCampaignId(campaignId);
    // Don't close menu if route selector is enabled - let user pick a route
    if (!showRouteSelector) {
      setIsOpen(false);
    }
    setHoveredCampaignId(null);
  };

  const handleRouteSelect = async (routeId: string, campaignId?: string) => {
    const targetCampaignId = campaignId || selectedCampaignId;
    if (targetCampaignId) {
      if (campaignId) {
        // If selecting from submenu, set campaign first
        setSelectedCampaign(campaignId);
      }
      await setActiveRoute(targetCampaignId, routeId);
      // Auto-load the route for viewing
      await loadRoute(routeId);
      setIsOpen(false);
      setShowRoutes(false);
      setHoveredCampaignId(null);
    }
  };

  // Handle route selection from inline submenu
  const handleInlineRouteSelect = async (routeId: string) => {
    const campaignId = expandedCampaignId || selectedCampaignId;
    if (campaignId) {
      // First select the campaign if different
      if (campaignId !== selectedCampaignId) {
        setSelectedCampaign(campaignId);
      }
      // Set as active route and persist
      await setActiveRoute(campaignId, routeId);
      // Auto-load the route for viewing
      await loadRoute(routeId);
      // Close the dropdown
      setIsOpen(false);
      setExpandedCampaignId(null);
    }
  };

  // Handle campaign hover to show routes submenu (desktop only)
  const handleCampaignHover = async (campaignId: string) => {
    if (showRouteSelector) {
      setHoveredCampaignId(campaignId);
      // Fetch routes for this campaign
      const routes = await fetchRouteHistory(campaignId);
      setSubmenuRoutes(routes || []);
    }
  };

  // Handle campaign click on mobile - drill down to routes
  const handleCampaignClickMobile = async (campaignId: string) => {
    if (showRouteSelector) {
      setIsLoadingRoutes(true);
      setDrillDownCampaignId(campaignId);
      const routes = await fetchRouteHistory(campaignId);
      setDrillDownRoutes(routes || []);
      setIsLoadingRoutes(false);
    } else {
      // No routes, just select campaign
      handleCampaignSelect(campaignId);
    }
  };

  // Go back from routes to campaigns
  const handleBackToCampaigns = () => {
    setDrillDownCampaignId(null);
    setDrillDownRoutes([]);
  };

  // Handle swipe down to close on mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (touchStart !== null && touchEnd !== null) {
      const distance = touchEnd - touchStart;
      const minSwipeDistance = 50; // minimum distance for swipe

      if (distance > minSwipeDistance) {
        // Swiped down - close the bottom sheet
        setIsOpen(false);
        setShowRoutes(false);
        setDrillDownCampaignId(null);
      }
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Breadcrumb Campaign Button - styled like browser tab folder */}
      <button
        className={`
          group
          relative
          inline-flex items-center gap-2
          px-3 md:px-4 py-1 md:py-1
          bg-gradient-to-r from-dark-surface/90 to-dark-surface/70
          hover:from-dark-surface hover:to-dark-surface/80
          border border-brand-500/20 hover:border-brand-500/40
          rounded-lg
          text-[11px] md:text-xs
          font-medium
          transition-all duration-200
          min-h-[28px] md:min-h-[18px]
          max-w-full
          shadow-sm shadow-black/10
          ${isOpen ? 'border-brand-500/50 shadow-brand-500/10' : ''}
        `}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="breadcrumb-campaign-selector"
        data-ux="action"
      >
        {/* Single-line layout: Campaign + Route on one row */}
        <div className="flex items-center gap-2 min-w-0 max-w-[320px] md:max-w-[440px]">
          {/* Campaign Name */}
          <span className="truncate text-brand-400 group-hover:text-brand-300">
            {selectedCampaign?.campaign_name || 'Select Campaign'}
          </span>

          {/* Route separator + name (inline) */}
          {showRouteSelector && activeRoute && (
            <>
              <span className="text-dark-border/50 flex-shrink-0">/</span>
              <RouteIcon className="w-2.5 h-2.5 text-amber-500 flex-shrink-0" />
              <span className="truncate text-dark-muted text-[10px] md:text-[11px]">
                {activeRoute.route_name}
              </span>
              <span className="text-dark-muted/60 flex-shrink-0 text-[10px] md:text-[11px]">
                ({activeRoute.total_stops})
              </span>
            </>
          )}
        </div>

        {/* Dropdown Indicator */}
        <ChevronDown
          className={`
            w-3.5 h-3.5 flex-shrink-0 text-dark-muted
            transition-transform duration-200
            group-hover:text-brand-400
            ${isOpen ? 'rotate-180 text-brand-400' : ''}
          `}
        />
      </button>

      {/* Mobile Bottom Sheet Backdrop */}
      {isOpen && (
        <div
          className="
            md:hidden
            fixed inset-0 bg-black/50
            z-[100]
            animate-in fade-in duration-200
          "
          onClick={() => {
            setIsOpen(false);
            setShowRoutes(false);
            setDrillDownCampaignId(null);
          }}
        />
      )}

      {/* Mobile Bottom Sheet or Desktop Dropdown */}
      {isOpen && (
        <div
          ref={bottomSheetRef}
          className="
            fixed md:absolute
            left-0 md:left-0
            bottom-0 md:top-full md:bottom-auto md:mt-2
            w-full md:min-w-[480px]
            bg-dark-surface
            border-t md:border border-dark-border/30
            rounded-t-2xl md:rounded-lg
            shadow-2xl md:shadow-xl
            z-[101] md:z-50
            max-h-[70vh] md:max-h-[600px]
            overflow-hidden
            animate-in slide-in-from-bottom md:fade-in duration-200 md:duration-200
          "
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile Drag Handle */}
          <div className="md:hidden flex justify-center py-2 bg-dark-bg border-b border-dark-border/30">
            <div className="w-12 h-1 bg-dark-muted/50 rounded-full" />
          </div>
          {/* Mobile Drill-Down: Routes View */}
          {drillDownCampaignId ? (
            <div className="animate-in slide-in-from-right duration-200">
              {/* Header with Back Button */}
              <div className="px-4 py-3 border-b border-dark-border/30 bg-dark-bg flex items-center gap-2">
                <button
                  onClick={handleBackToCampaigns}
                  className="flex items-center gap-1 text-brand-500 hover:text-brand-400 transition-all duration-150"
                  aria-label="Back to campaigns"
                >
                  <ChevronDown className="w-4 h-4 rotate-90" />
                </button>
                <h3 className="text-xs font-semibold text-dark-muted uppercase tracking-wide flex-1">
                  Select Route
                </h3>
              </div>

              {/* Routes List */}
              <div className="py-2 max-h-[336px] overflow-y-auto">
                {isLoadingRoutes ? (
                  <div className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500 mx-auto mb-2"></div>
                    <p className="text-xs text-dark-muted">Loading routes...</p>
                  </div>
                ) : drillDownRoutes.length === 0 ? (
                  <div className="px-4 py-8 text-center text-dark-muted text-sm">
                    No routes available
                  </div>
                ) : (
                  drillDownRoutes.map(route => (
                    <button
                      key={route.route_id}
                      className="
                        w-full
                        px-4 py-3
                        flex items-center gap-3
                        hover:bg-dark-surface/50
                        active:bg-dark-surface/60
                        transition-all duration-150
                        text-left
                        min-h-[44px]
                      "
                      onClick={() => handleRouteSelect(route.route_id, drillDownCampaignId)}
                    >
                      <MapPin className="w-4 h-4 text-brand-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm text-dark-text font-medium">
                          {route.route_name}
                        </div>
                        <div className="text-xs text-dark-muted">
                          {route.total_stops} stops • {route.total_weeks} weeks
                        </div>
                      </div>
                      {activeRouteId === route.route_id && selectedCampaignId === drillDownCampaignId && (
                        <Check className="w-4 h-4 text-brand-500 flex-shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            /* Campaigns List */
            <>
          {/* Campaigns Section */}
          {!showRoutes ? (
            <>
              <div className="px-4 py-3 border-b border-dark-border/30">
                <h3 className="text-xs font-semibold text-dark-muted uppercase tracking-wide">
                  Select Campaign
                </h3>
              </div>

              <div className="py-2 max-h-[200px] overflow-y-auto scrollbar-dark">
                {sortedCampaigns.length === 0 ? (
                  <div className="px-4 py-8 text-center text-dark-muted text-sm">
                    No campaigns available
                  </div>
                ) : (
                  sortedCampaigns.map(camp => {
                    const isSelected = selectedCampaignId === camp.campaign_id;
                    const isExpanded = expandedCampaignId === camp.campaign_id;
                    const campActiveRouteId = activeRoutesByCampaign[camp.campaign_id] || null;

                    return (
                      <div key={camp.campaign_id}>
                        <button
                          className={`
                            w-full
                            px-3 py-1.5
                            flex items-center justify-between gap-2
                            hover:bg-dark-surface/50
                            active:bg-dark-surface/60
                            transition-all duration-150
                            text-left
                            min-h-[40px] md:min-h-[28px]
                            ${isSelected ? 'bg-dark-surface/50' : ''}
                          `}
                          onClick={() => {
                            // On mobile with routes: drill down, on desktop: select campaign
                            if (showRouteSelector && window.innerWidth < 768) {
                              handleCampaignClickMobile(camp.campaign_id);
                            } else {
                              handleCampaignSelect(camp.campaign_id);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {/* Selected indicator */}
                            {isSelected ? (
                              <Check className="w-3.5 h-3.5 text-brand-500 flex-shrink-0" />
                            ) : (
                              <div className="w-3.5 h-3.5 flex-shrink-0" />
                            )}

                            <span className="truncate text-xs text-dark-text">
                              {camp.campaign_name}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span
                              className={`
                                px-2 py-1
                                text-[10px] font-medium
                                rounded border
                                ${getStatusBadgeColor(camp.status)}
                              `}
                            >
                              {camp.status.toUpperCase()}
                            </span>
                          </div>
                        </button>

                      </div>
                    );
                  })
                )}
              </div>

              {/* Route History Submenu - shown AFTER all campaigns for selected campaign (desktop) */}
              {showRouteSelector && selectedCampaignId && (
                <div className="hidden md:block mx-3 mb-2">
                  <RouteHistorySubmenu
                    routeHistory={routeHistoryArray.filter(r => r.campaign_id === selectedCampaignId)}
                    activeRouteId={activeRoutesByCampaign[selectedCampaignId] || null}
                    isLoading={isLoadingRoutes}
                    onSelectRoute={handleInlineRouteSelect}
                    limit={10}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              {/* Route Selection View */}
              <div className="px-4 py-3 border-b border-dark-border/30 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-dark-muted uppercase tracking-wide">
                  Select Route
                </h3>
                <button
                  className="
                    text-xs text-brand-600 hover:text-brand-500
                    flex items-center gap-1
                    min-h-[44px] md:min-h-[32px]
                  "
                  onClick={() => setShowRoutes(false)}
                >
                  <ChevronDown className="w-3 h-3 rotate-90" />
                  Back to Campaigns
                </button>
              </div>

              <div className="py-2">
                {routeHistoryArray.length === 0 ? (
                  <div className="px-4 py-8 text-center text-dark-muted text-sm">
                    No routes available for this campaign
                  </div>
                ) : (
                  routeHistoryArray.map(route => (
                    <button
                      key={route.route_id}
                      className="
                        w-full
                        px-4 py-3
                        flex items-center justify-between gap-3
                        hover:bg-dark-surface/50
                        active:bg-dark-surface/60
                        transition-all duration-150
                        text-left
                        min-h-[44px] md:min-h-[36px]
                      "
                      onClick={() => handleRouteSelect(route.route_id)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {activeRouteId === route.route_id && (
                          <Check className="w-4 h-4 text-brand-500 flex-shrink-0" />
                        )}
                        <MapPin className="w-4 h-4 text-dark-muted flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-sm text-dark-text">
                            {route.route_name}
                          </div>
                          <div className="text-xs text-dark-muted">
                            {route.total_stops} stops • {route.total_weeks} weeks
                          </div>
                        </div>
                      </div>

                      {activeRouteId === route.route_id && (
                        <span className="text-xs text-brand-500 flex-shrink-0">
                          Active
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
};
